import React, {
  useRef,
  useCallback,
  useEffect,
  cloneElement,
} from 'react';

import {useOnClickOff} from '@newsela/angelou';
import {css} from 'emotion';
import PropTypes from 'prop-types';

import {
  absoluteContainerCss,
  subNavMenuPositionCss,
  getContainerPositionCss,
} from 'static/three-oh/src/components/GlobalLayout/GlobalLayoutV2/NavigationBar/shared/NavButtonWithPopout/NavButtonWithPopout.styles';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

const NavButtonWithPopout = ({
  id,
  isOpen,
  setIsOpen,
  position,
  isSubNavMenu,
  rootCustomCss = {},
  ButtonComponent,
  PopoutComponent,
  onBlur,
  managesFocusInternally = false,
}) => {
  const triggerRef = useRef(null);
  const popoutRef = useRef(null);
  const focusablesRef = useRef([]);
  const focusedRef = useRef(null);

  const closeMenu = (e) => {
    const target = e.target;
    if (!target) return;

    const isEventFromSelf =
      target.id === id || target.classList.contains(id);

    if (!isEventFromSelf) {
      setIsOpen(false);
    }
  };

  const clickOffRef = useOnClickOff(closeMenu);

  useEffect(() => {
    const onFocusIn = (e) => {
      focusedRef.current = e.target;
    };

    window.addEventListener('focusin', onFocusIn, true);
    return () => window.removeEventListener('focusin', onFocusIn, true);
  }, []);

  useEffect(() => {
    if (isOpen && !managesFocusInternally) {
      popoutRef.current?.focus();
    }
  }, [isOpen, managesFocusInternally]);

  const isVisible = (el) => {
    if (!el) return false;

    try {
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }
      return el.getClientRects().length > 0;
    } catch {
      return true;
    }
  };

  const collectFocusables = (container) => {
    const nodes = Array.from(
      container.querySelectorAll(FOCUSABLE_SELECTOR),
    );

    return nodes.filter(isVisible);
  };

  const combinedRef = useCallback(
    (node) => {
      popoutRef.current = node;

      if (typeof clickOffRef === 'function') {
        clickOffRef(node);
      } else if (clickOffRef && 'current' in clickOffRef) {
        clickOffRef.current = node;
      }

      if (!node) {
        focusablesRef.current = [];
        return;
      }

      if (!managesFocusInternally) {
        focusablesRef.current = collectFocusables(node);
      }
    },
    [clickOffRef],
  );

  const closeOnEscape = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    event.stopPropagation();
    triggerRef.current?.focus();
    setIsOpen(false);
  };

  const onPopoutKeyDown = (e) => {
    const focusable = focusablesRef.current;
    if (!focusable.length) return;

    const activeEl = document.activeElement;
    const currentIndex = focusable.indexOf(activeEl);

    // Handle Tab and Arrows
    const isNext = e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey);
    const isPrev = e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey);

    if (isNext) {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % focusable.length;
      focusable[nextIndex]?.focus();
    }

    if (isPrev) {
      e.preventDefault();
      const prevIndex = currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
      focusable[prevIndex]?.focus();
    }
  };

  const onRootKeyDown = (event) => {
    if (event.key === 'Escape') {
      closeOnEscape(event);
      return;
    }

    if (!isOpen) return;
    if (focusedRef.current !== triggerRef.current) return;

    const focusables = focusablesRef.current;
    if (!focusables.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      focusables[0]?.focus();
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      focusables[focusables.length - 1]?.focus();
    }
  };

  const {top, right, bottom, left} = position;

  let popoutStyle = {
    ...absoluteContainerCss,
    top,
    right,
    bottom,
    left,
  };

  if (isSubNavMenu) {
    popoutStyle = {
      ...popoutStyle,
      ...subNavMenuPositionCss,
    };
  }

  const buttonWithRef = cloneElement(ButtonComponent, {
    ref: triggerRef,
    id,
    type: 'button',
    'aria-haspopup': 'menu',
    'aria-expanded': isOpen,
  });

  return (
    <li
      onKeyDown={onRootKeyDown}
      className={css({
        ...getContainerPositionCss(isSubNavMenu),
        ...rootCustomCss,
      })}
    >
      {buttonWithRef}

      {isOpen && (
        <div
          onBlur={onBlur}
          ref={combinedRef}
          id={`${id}-menu`}
          tabIndex={managesFocusInternally ? -1 : 0}
          role="menu"
          aria-labelledby={id}
          onKeyDown={onPopoutKeyDown}
          data-testid="navmenupopout-container"
          className={css(popoutStyle)}
        >
          {PopoutComponent}
        </div>
      )}
    </li>
  );
};

NavButtonWithPopout.propTypes = {
  id: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  isSubNavMenu: PropTypes.bool,
  rootCustomCss: PropTypes.object,
  position: PropTypes.object.isRequired,
  ButtonComponent: PropTypes.element.isRequired,
  PopoutComponent: PropTypes.element.isRequired,
  managesFocusInternally: PropTypes.bool,
  onBlur: PropTypes.func,
};

NavButtonWithPopout.displayName = 'NavButtonWithPopout';

export {NavButtonWithPopout};
